import React from 'react';
import { Building, Building2, PencilRuler, Ruler, Compass, HardHat, RefreshCw, Zap, Calculator, ClipboardList, FileText, FileCheck } from 'lucide-react';
import { services, servicesProcess, servicesWhyChooseUs } from '../data';
import { Button } from '../components/ui/button';
import { Breadcrumb } from '../components/Breadcrumb';
import SEO from '../components/SEO';
const iconMap = {
  PencilRuler: PencilRuler,
  Ruler: Ruler,
  Zap: Zap,
  Building2: Building2,
  RefreshCw: RefreshCw,
  Compass: Compass,
  Building: Building,
  HardHat: HardHat,
  Calculator: Calculator,
  ClipboardList: ClipboardList,
  FileText: FileText,
  FileCheck: FileCheck
};

const Services = ({ onQuotationClick }) => {
  return (
    <div>
      <Breadcrumb items={[{ label: 'Services' }]} />
      
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/3034343/pexels-photo-3034343.jpeg?auto=compress&cs=tinysrgb&w=1200&q=40"
          alt="Services"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 sm:mb-4">
            Our Services
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Comprehensive construction solutions from concept to completion, delivered with quality and attention to detail
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-gray-100 overflow-hidden"
                >
                  <div className="p-4 sm:p-6 md:p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-brand-100 to-brand-50 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-brand-600" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  <div className="h-1 bg-gradient-to-r from-brand-600 to-brand-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              A seamless journey from initial consultation to the keys to your dream home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-11">
            {servicesProcess.map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-20 h-20 bg-gradient-to-br from-brand-600 to-brand-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-3xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-brand-600 to-transparent -ml-24" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              Why Choose Us
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Ensures a blend of creative innovation, technical precision, and client-focused collaboration to turn visions into reality
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {servicesWhyChooseUs.map((item, index) => (
              <div
                key={index}
                className="flex gap-4 sm:gap-6 p-4 sm:p-6 md:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                    <div className="w-3 h-3 bg-brand-600 rounded-full" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-brand-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-600 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-10">
            Let's discuss how we can bring your construction project to life
          </p>
          <Button
            onClick={onQuotationClick}
            size="lg"
            className="bg-brand-600 hover:bg-brand-700 text-white px-8 sm:px-12 py-4 sm:py-6 text-base sm:text-lg font-medium shadow-2xl hover:shadow-brand-600/50 transition-all duration-300 hover:scale-105"
          >
            Get Started Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
